import { useState, useEffect, useCallback } from 'react';

interface LightboxImage {
  src: string;
  alt: string;
  label?: string;
}

interface LightboxProps {
  images: LightboxImage[];
  initialIndex: number;
  onClose: () => void;
  title?: string;
}

const Lightbox = ({ images, initialIndex, onClose, title }: LightboxProps) => {
  const [current, setCurrent] = useState(initialIndex);

  const goNext = useCallback(() => {
    setCurrent((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goNext, goPrev]);

  const img = images[current];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer z-10"
        onClick={onClose}
        aria-label="Sluiten"
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      {/* Title & counter */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10 pointer-events-none">
        {title && (
          <span className="text-white/80 text-sm font-medium tracking-wide">{title}</span>
        )}
        <span className="text-white/50 text-xs">
          {current + 1} / {images.length}
        </span>
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer z-10"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Vorige"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
      )}

      {/* Image */}
      <div
        className="relative max-w-5xl w-full mx-16 flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-black">
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            className="w-full max-h-[80vh] object-contain object-center animate-fade-in"
            style={{ animation: 'fadeIn 0.25s ease' }}
          />
          {img.label && (
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
              <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                {img.label}
              </span>
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 max-w-full px-2">
            {images.map((im, idx) => (
              <button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                  idx === current ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-80'
                }`}
                aria-label={`Afbeelding ${idx + 1}`}
              >
                <img src={im.src} alt={im.alt} className="w-full h-full object-cover object-top" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer z-10"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Volgende"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
};

export default Lightbox;
